import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Instagram } from "lucide-react";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Packages", id: "packages" },
  { label: "Book", id: "booking" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 border-b transition-colors duration-300 ${
        scrolled ? "bg-ink/70 backdrop-blur-xl border-white/10" : "bg-transparent border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center group"
        >
          <img
            src="/logo.png"
            alt="Aero Detail Lab"
            className="h-12 md:h-14 w-auto transition-transform duration-500 group-hover:scale-105"
          />
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono text-xs uppercase tracking-[0.18em] text-purple hover:text-lime transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <a
            href="https://instagram.com/aero.detail.lab"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-instagram"
            aria-label="Instagram"
            className="text-white/70 hover:text-lime transition-colors duration-300"
          >
            <Instagram size={18} />
          </a>
          <a
            href="https://tiktok.com/@aero.detail.lab"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-tiktok"
            aria-label="TikTok"
            className="text-white/70 hover:text-lime transition-colors duration-300"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <path d="M16.5 3c.3 2.1 1.5 3.6 3.5 3.9V9c-1.3.1-2.5-.3-3.6-1v6.7c0 3.4-2.5 5.6-5.6 5.6-2.9 0-5.3-2.2-5.3-5.2 0-3.1 2.5-5.2 5.6-4.9v2.6c-.4-.1-.9-.2-1.3-.1-1.2.1-2 .9-2 2.2 0 1.4 1 2.3 2.4 2.3 1.5 0 2.5-1 2.5-2.9V3h3.4z" />
            </svg>
          </a>
          <button
            data-testid="nav-book-btn"
            onClick={() => go("booking")}
            className="btn-fill border border-lime text-lime px-5 py-2.5 font-mono text-xs uppercase tracking-[0.18em]"
          >
            Request Slot
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white p-2"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-xl border-t border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="text-left font-mono text-sm uppercase tracking-[0.18em] text-white/80"
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
